/**
 * **How a sleep or mood line is painted** — `ReadoutChart`'s `banding` for one wellbeing signal:
 * under the low cut in the danger ink, over the high cut in the good ink, and in between the chart's
 * own series colour.
 *
 * Promoted out of `pages/client-detail`'s Trendy tab on 27 September 2026, on its second consumer:
 * the plan overview draws the same two lines in its week-by-week table and its strip. The cuts are
 * `model/wellbeingBands`' — the roster's columns read the same ones — and this file only turns them
 * into paint.
 *
 * The stripes and the mark predicate are BOTH taken, and they are not the same question: the
 * stripes say where the colour changes, and `wellbeingBand` says which side a reading sitting
 * exactly on a cut falls. See `ChartBanding`.
 */

import type { ChartBanding } from '~/shared/ui/chart/ReadoutChart';

import {
  wellbeingBand,
  wellbeingBandSpans,
  type WellbeingBand,
  type WellbeingLevels,
  type WellbeingSignal,
} from '../model/wellbeingBands';

/**
 * The two verdict inks. `middling` is absent on purpose: the neutral band is the CHART's own series
 * colour, and only the caller knows which it is.
 */
const VERDICT_INK: Readonly<Record<Exclude<WellbeingBand, 'middling'>, string>> = {
  low: 'var(--danger)',
  high: 'var(--good)',
};

/**
 * @param base The series colour the neutral band keeps, as a token reference.
 * @param levels The trainer's levels — `lib/useWellbeingLevels`.
 */
export function wellbeingBanding(
  base: string,
  signal: WellbeingSignal,
  levels: WellbeingLevels,
): ChartBanding {
  return {
    spans: wellbeingBandSpans(signal, levels).map((span) => ({
      from: span.from,
      to: span.to,
      colour: span.band === 'middling' ? base : VERDICT_INK[span.band],
    })),
    inkOf: (value: number) => {
      const band = wellbeingBand(value, signal, levels);
      return band === 'middling' ? base : VERDICT_INK[band];
    },
  };
}
